import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-email-step',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
    MatButtonModule, TranslateModule, LoadingSpinnerComponent,
  ],
  host: { class: 'flex flex-col flex-1 min-h-0' },
  template: `
    <form [formGroup]="form" (ngSubmit)="onSubmit()" class="flex flex-col flex-1 min-h-0">
      <div class="flex-1 overflow-auto min-h-0">
        <h2 class="headline-medium text-primary mb-2 hidden md:block" style="font-family: 'DM Sans', sans-serif">{{ 'Add a contact email' | translate }}</h2>
        <p class="body-large text-on-surface-variant mb-6">{{ "Please enter a valid email address. We'll use it for things like password resets, notifications, and important account updates" | translate }}</p>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>{{ 'Email address' | translate }}</mat-label>
          <input
            matInput
            type="email"
            formControlName="email"
            [placeholder]="'Enter your email address' | translate"
          />
          @if (form.get('email')?.hasError('required') && form.get('email')?.touched) {
            <mat-error>{{ 'Email is required' | translate }}</mat-error>
          }
          @if (form.get('email')?.hasError('email') && form.get('email')?.touched) {
            <mat-error>{{ 'Please enter a valid email address' | translate }}</mat-error>
          }
        </mat-form-field>
      </div>

      <div class="shrink-0 pt-4">
        <div class="flex items-center justify-between gap-4">
          <button mat-stroked-button type="button" class="w-1/2" (click)="remindLater.emit()">
            {{ 'Remind me later' | translate }}
          </button>

          <button
            mat-flat-button
            color="primary"
            type="submit"
            class="w-1/2"
            [disabled]="form.invalid || loading"
          >
            <app-loading-spinner [loading]="loading" [diameter]="20">
              {{ 'Continue' | translate }}
            </app-loading-spinner>
          </button>
        </div>

        <div class="text-center pt-2">
          <button mat-button type="button" class="text-on-surface-variant !text-sm" (click)="doNotAskAgain.emit()">
            {{ 'Do not ask me again' | translate }}
          </button>
        </div>
      </div>
    </form>
  `,
})
export class EmailStepComponent implements OnInit {
  @Input() loading = false;
  @Input() initialEmail = '';
  @Output() submitEmail = new EventEmitter<string>();
  @Output() remindLater = new EventEmitter<void>();
  @Output() doNotAskAgain = new EventEmitter<void>();

  form!: FormGroup;

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    this.submitEmail.emit(this.form.value.email);
  }
}
